<template>
  <div
    :class="[componentClass, $attrs.class]"
    :style="[componentStyle, $attrs.style]"
  >
    <label
      :class="labelClass"
      :for="sluggedLabel"
    >
      {{ label }}
    </label>
    <div :class="inputWrapperClass">
      <font-awesome-icon
        v-if="hasIcon && isProcessing"
        :icon="$v7[iconStyle][isProcessingIconName]"
        spin
      />
      <font-awesome-icon
        v-else-if="hasIcon"
        :icon="$v7[iconStyle][iconName]"
      />

      <input
        :id="sluggedLabel"
        v-bind="inputAttrs"
        ref="field"
        :autofocus="autofocus"
        :class="inputClass"
        :disabled="isDisabled"
        :name="sluggedLabel"
        :placeholder="placeholder"
        :readonly="isReadOnly"
        :required="isRequired"
        :type="type"
        :value="currentValue.value"
        @click="handleClick"
        @input="updateValue"
        @blur="onBlur"
        @focus="$emit('focus')"
        @keydown.enter.prevent="$emit('enter')"
      />
    </div>

    <p
      v-if="tip"
      class="tip margin-top-2xs margin-bottom-0"
    >
      {{ tip }}
    </p>
  </div>
</template>

<script>
  import sluggify from '../../utils/sluggify'

  export default {
    name: 'TextField',

    inheritAttrs: false,

    props: {
      autofocus: {
        type: Boolean,
        default: false
      },
      customStyle: {
        type: Object,
        default: () => {}
      },
      iconPlacement: {
        type: String,
        default: 'before'
      },
      iconName: {
        type: String,
        default: null
      },
      iconStyle: {
        type: String,
        default: 'solid'
      },
      inputClass: {
        type: String,
        default: null
      },
      isDisabled: {
        type: Boolean,
        default: false
      },
      isProcessing: {
        type: Boolean,
        default: false
      },
      isProcessingIconName: {
        type: String,
        default: 'faSpinnerThird'
      },
      isMinimal: {
        type: Boolean,
        default: false
      },
      isReadOnly: {
        type: Boolean,
        default: false
      },
      isRequired: {
        type: Boolean,
        default: true
      },
      isRounded: {
        type: Boolean,
        default: false
      },
      isValid: {
        type: Boolean,
        default: true
      },
      label: {
        type: String,
        required: true
      },
      placeholder: {
        type: String,
        default: null
      },
      tip: {
        type: String
      },
      type: {
        type: String,
        default: 'text'
      },
      value: {
        type: Object,
        default: () => {
          return { valid: true, value: '' }
        }
      },

      // Vue 3's `v-model` binds `modelValue` and listens for
      // `update:modelValue`; Vue 2's bound `value` and listened for `input`.
      // Both are accepted so the 157 `v-model` call sites work unchanged while
      // the handful that pass `:value` and `@input` explicitly keep working too.
      modelValue: {
        type: Object,
        default: null
      }
    },

    // Declared per CMP-15: an undeclared native-named emit runs the parent's handler twice.
    emits: ['blur', 'click', 'enter', 'focus', 'input', 'update:modelValue', 'validity'],

    computed: {
      // `modelValue` when bound through `v-model`, otherwise the explicit
      // `value` prop.
      currentValue() {
        return this.modelValue || this.value
      },

      componentStyle() {
        return {
          ...this.customStyle
        }
      },

      componentClass() {
        return { danger: !this.isValid }
      },

      // Vue 2 kept `class` and `style` out of `$attrs` and put them on the root
      // element even under `inheritAttrs: false`; Vue 3 puts them IN `$attrs`,
      // so spreading it onto the input moved every consumer's class down a
      // level. A margin utility then replaced the input's own margin instead of
      // adding to the wrapper's, which is how `/admin/accounts` came to render
      // 3.5px shorter and `/admin/quotes/create` 32px. Class and style go to the
      // root, as before; everything else still reaches the input.
      inputAttrs() {
        // eslint-disable-next-line no-unused-vars
        const { class: className, style, ...rest } = this.$attrs
        return rest
      },

      hasIcon() {
        return this.iconName
      },

      inputWrapperClass() {
        return [{ 'with-icon-before': this.hasIcon && this.iconPlacement == 'before' }, { 'with-icon-after': this.hasIcon && this.iconPlacement == 'after' }]
      },

      labelClass() {
        return ['margin-bottom-2xs', { 'sr-only': this.isMinimal }]
      },

      sluggedLabel() {
        return sluggify(this.label)
      }
    },

    methods: {
      onBlur(event) {
        this.$emit('blur')
        this.updateValue(event)
      },

      handleClick(event) {
        this.$emit('click', event)
      },

      updateValue(event) {
        const next = {
          valid: event.target.validity.valid,
          value: event.target.value
        }

        this.$emit('update:modelValue', next)
        this.$emit('input', next)

        this.$emit('validity', event.target.validity)
      },

      focus() {
        try {
          this.$refs.field.focus()
        } catch (e) {
          // noop
        }
      }
    }
  }
</script>
